import { useEffect, useRef, useState, useCallback } from 'react'
import {
    MISSILE_RISE_MS, MISSILE_RESPONSE_MS, MISSILE_EXCHANGE_MS, MISSILE_EXCHANGE_GAP_MS,
    MISSILE_SURPLUS_RISE_MS, MISSILE_SURPLUS_STAGGER_MS,
} from '../../../rules/gameConfig'
import { ensureSlots } from '../../../rules/gameLogic'
import { hexDist2 } from '../../../rules/hexGrid'
import { launchStructureMote, cancelAllMotes } from '../damageMotes'
import { playSound, fadeOutAll } from '../../../utils/soundManager'
import { FIRE_SWEEP_MS_PER_CELL, MOTE_STRUCT_LAG_MS, MISSILE_SHAKE_MS, attackerSideOf } from '../battleFx'
import { shotOrigins } from './battle'

// ─── Hex board — the reveal's board-state effects ─────────────────────────────
//
// useGameState drives the reveal (handleReveal): it steps the clash props
// corridor by corridor and SWAPS the Grids (selfHf / oppHf) as each strike
// lands. This hook watches those swaps and the clash props and turns them
// into the board's animation state — nothing here changes game state:
//
//   • fresh damage    — cells destroyed by the latest swap, per side, plus the
//                       aim they emanated from: each fades in as the fire ring
//                       reaches it (fireDelayMs, hex Euclidean distance), and
//                       a killed structure's silhouette flies to the kill bar
//                       once the sweep has consumed its footprint
//   • fresh scans     — cells newly revealed by drone scans, sweeping in from
//                       each drone's target
//   • the fire ring   — one expanding ring per crater swap
//   • tile firing     — on the duel's launch schedule each drone tile recoils
//                       as its shot goes airborne and fades once its last shot
//                       is away; a mature Base recoils on the Grid per shot
//                       (baseAnim) and fades once spent, holding its fade for
//                       the rest of the reveal (spentBases)
//   • collapse        — a construction whose cell the swap destroyed flashes
//                       and blasts as one bar (conCollapse)
//   • SFX             — launches, collisions and strikes on the same timeline

// A cell ignites this many ms after the strike lands: its centre-to-centre
// distance from the aim, in cells, at the sweep's pace.
export const fireDelayMs = (s, originS) =>
    Math.round((Math.sqrt(hexDist2(s, originS)) / Math.sqrt(3)) * FIRE_SWEEP_MS_PER_CELL)

// The nearest of several scan targets, for the scan sweep.
export const scanDelayMs = (s, origins) =>
    (origins?.length ? Math.min(...origins.map(o => Math.sqrt(hexDist2(s, o)) / Math.sqrt(3))) * FIRE_SWEEP_MS_PER_CELL : 0)

export function useReveal({
    selfHf, oppHf, selfBoard, oppBoard, isAnimating,
    activeClashId, clashResult, activeClashContext, activeClashWinner,
    activeClashSelfStrength, activeClashOppStrength, activeClashAbsorbed, activeClashAimS,
    activeClashSelfTargetIndex, activeClashOppTargetIndex, targetPlots,
    droneFlights, onStructureKilled,
}) {
    // ── Fresh damage / scans (per side) ────────────────────────────────────
    const [fresh, setFresh] = useState({ self: new Set(), opp: new Set() })
    const [fireOrigin, setFireOrigin] = useState({ self: null, opp: null })
    const [freshScan, setFreshScan] = useState({ self: new Set(), opp: new Set() })
    const [scanOrigins, setScanOrigins] = useState({ self: [], opp: [] })
    const [fireRing, setFireRing] = useState(null)
    const fireRingIdRef = useRef(0)
    const [conCollapse, setConCollapse] = useState({ self: null, opp: null })
    const conCollapseIdRef = useRef(0)
    const aimRef = useRef(activeClashAimS)
    useEffect(() => { aimRef.current = activeClashAimS }, [activeClashAimS])
    const flightsRef = useRef(droneFlights)
    useEffect(() => { flightsRef.current = droneFlights }, [droneFlights])
    const onKilledRef = useRef(onStructureKilled)
    useEffect(() => { onKilledRef.current = onStructureKilled }, [onStructureKilled])

    // Silhouettes scheduled vs landed this reveal, per side, so each in-flight
    // one aims at its own kill-bar icon even when several fly at once.
    const ledgerRef = useRef({ self: { scheduled: 0, landed: 0 }, opp: { scheduled: 0, landed: 0 } })
    useEffect(() => { if (!isAnimating) ledgerRef.current = { self: { scheduled: 0, landed: 0 }, opp: { scheduled: 0, landed: 0 } } }, [isAnimating])
    useEffect(() => () => cancelAllMotes(), [])

    const launchKillMotes = useCallback((side, hf, prevHf, origin) => {
        if (origin == null || !onKilledRef.current) return
        const barSide = side === 'self' ? 'you' : 'opp'
        const ledger = ledgerRef.current[side]
        for (const st of Object.values(hf.structures ?? {})) {
            const fp = st.footprint ?? []
            if (!fp.length) continue
            const deadNow = fp.every(f => hf.subplots[f]?.destroyed)
            const deadBefore = fp.every(f => prevHf.subplots[f]?.destroyed)
            if (!deadNow || deadBefore) continue
            const ordinal = ledger.scheduled++
            launchStructureMote({
                side,
                delayMs: Math.max(...fp.map(f => fireDelayMs(f, origin))) + MOTE_STRUCT_LAG_MS,
                getFromEls: () => fp.map(f => document.querySelector(`[data-mote-s="${side}-${f}"]`)).filter(Boolean),
                getToEl: () => {
                    const intact = document.querySelectorAll(`[data-kill-side="${barSide}"] [data-intact="true"]`)
                    return intact[ordinal - ledger.landed] ?? intact[intact.length - 1]
                        ?? document.querySelector(`[data-kill-side="${barSide}"]`)
                },
                onLand: () => { ledger.landed++; onKilledRef.current?.(side) },
            })
        }
    }, [])

    const diff = useCallback((side, hf, prev) => {
        if (!hf?.subplots || !prev?.subplots) return
        const destroyed = new Set()
        hf.subplots.forEach((c, s) => { if (c?.destroyed && !prev.subplots[s]?.destroyed) destroyed.add(s) })
        if (destroyed.size) {
            const origin = aimRef.current
            setFresh(f => ({ ...f, [side]: destroyed }))
            setFireOrigin(o => ({ ...o, [side]: origin }))
            // Fresh damage on a construction's footprint → the whole bar
            // falls, on the collapse clock (BoardGrid's diffConCollapse).
            const cells = new Map()
            for (const st of Object.values(hf.broods ?? {})) {
                if (!st.underConstruction) continue
                const fp = st.footprint ?? []
                const hit = fp.filter(f => destroyed.has(f))
                if (!hit.length) continue
                const delay = origin != null ? Math.max(...hit.map(f => fireDelayMs(f, origin))) : 0
                fp.forEach(f => cells.set(f, delay))
            }
            setConCollapse(c => ({ ...c, [side]: cells.size ? { id: ++conCollapseIdRef.current, cells } : null }))
            if (origin != null) {
                let maxDist = 0
                for (const s of destroyed) maxDist = Math.max(maxDist, Math.sqrt(hexDist2(s, origin)) / Math.sqrt(3))
                setFireRing({ id: ++fireRingIdRef.current, side, aimS: origin, maxDist })
            }
            launchKillMotes(side, hf, prev, origin)
        }
        const scanned = new Set()
        hf.subplots.forEach((c, s) => { if (c?.scanned && !prev.subplots[s]?.scanned) scanned.add(s) })
        if (scanned.size) {
            const flights = flightsRef.current
            const origins = (side === 'self' ? flights?.opp : flights?.self) ?? []
            setFreshScan(f => ({ ...f, [side]: scanned }))
            setScanOrigins(o => ({ ...o, [side]: origins.map(f => f.targetS).filter(t => t != null) }))
        }
    }, [launchKillMotes])

    const prevSelfRef = useRef(selfHf)
    const prevOppRef = useRef(oppHf)
    useEffect(() => { const prev = prevSelfRef.current; prevSelfRef.current = selfHf; diff('self', selfHf, prev) }, [selfHf, diff])
    useEffect(() => { const prev = prevOppRef.current;  prevOppRef.current  = oppHf;  diff('opp',  oppHf,  prev) }, [oppHf, diff])

    // ── Tile firing on the duel schedule ───────────────────────────────────
    // Both carry the clash's corridor (col): only ITS tiles recoil and fade —
    // the slot sets are per corridor, never board-wide.
    const [launched, setLaunched] = useState({ col: null, self: new Set(), opp: new Set() })
    const [shake, setShake] = useState({ col: null, self: null, opp: null })
    // The firing Bases' Grid animation per side: { centerS, shakeKey, spent }
    // — centerS is the Base whose shot is airborne (its cells recoil, shakeKey
    // bumps per shot), spent the centres whose last shot is away. baseAnim is
    // per clash; spentBases remembers every fired Base for the whole reveal
    // so its fade holds across later clashes.
    const [baseAnim, setBaseAnim] = useState({ self: null, opp: null })
    const [spentBases, setSpentBases] = useState({ self: new Set(), opp: new Set() })
    useEffect(() => { if (!isAnimating) setSpentBases({ self: new Set(), opp: new Set() }) }, [isAnimating])
    useEffect(() => {
        if (!isAnimating || !activeClashId || clashResult) {
            if (!clashResult) { setLaunched({ col: null, self: new Set(), opp: new Set() }); setBaseAnim({ self: null, opp: null }) }
            setShake({ col: null, self: null, opp: null })
            return undefined
        }
        const col = selfBoard?.findIndex(bf => bf.id === activeClashId) ?? -1
        if (col < 0) return undefined
        setLaunched({ col, self: new Set(), opp: new Set() })
        setShake({ col, self: null, opp: null })
        setBaseAnim({ self: null, opp: null })
        const numSelf = activeClashSelfStrength ?? 0, numOpp = activeClashOppStrength ?? 0
        const pairCount = Math.min(numSelf, numOpp)
        const surplusStart = pairCount * MISSILE_EXCHANGE_MS
        const defenderSide = attackerSideOf(activeClashContext, activeClashWinner) === 'self' ? 'opp' : 'self'
        const winnerHasTarget = activeClashWinner === 'self'
            ? ((targetPlots?.[col] ?? null) !== null || (activeClashSelfTargetIndex ?? null) !== null)
            : activeClashWinner === 'opp' ? (activeClashOppTargetIndex ?? null) !== null : false
        const launchTimeOf = (side, i) => (i < pairCount || !(side === activeClashWinner && winnerHasTarget))
            ? i * MISSILE_EXCHANGE_MS + (side === defenderSide ? MISSILE_RESPONSE_MS : 0)
            : surplusStart + (i - pairCount) * MISSILE_SURPLUS_STAGGER_MS
        const timers = []
        const schedule = (side, count, origins) => {
            for (let i = 0; i < count; i++) {
                const o = origins[i]
                if (!o) continue
                const t = launchTimeOf(side, i)
                const next = origins[i + 1]
                if (!o.unit) {
                    // A Base's shot: its bar recoils; once its last shot is
                    // away it fades, and stays faded for the rest of the reveal.
                    const centerS = o.centerS ?? null
                    timers.push(setTimeout(() => setBaseAnim(a => ({ ...a, [side]: { centerS, shakeKey: (a[side]?.shakeKey ?? 0) + 1, spent: a[side]?.spent ?? [] } })), t))
                    const nextSame = i + 1 < count && next && !next.unit && (next.centerS ?? null) === centerS
                    if (!nextSame) {
                        timers.push(setTimeout(() => {
                            setBaseAnim(a => ({ ...a, [side]: { ...(a[side] ?? { shakeKey: 0 }), centerS: null, spent: [...(a[side]?.spent ?? []), centerS] } }))
                            setSpentBases(sb => { const n = new Set(sb[side]); n.add(centerS); return { ...sb, [side]: n } })
                        }, t + MISSILE_SHAKE_MS))
                    }
                    continue
                }
                const lastOfSlot = !(i + 1 < count && next?.unit && next.p === o.p)
                timers.push(setTimeout(() => setShake(s => ({ ...s, [side]: o.p })), t))
                timers.push(setTimeout(() => setShake(s => (s[side] === o.p ? { ...s, [side]: null } : s)), t + MISSILE_SHAKE_MS))
                if (lastOfSlot) timers.push(setTimeout(() => setLaunched(l => { const n = new Set(l[side]); n.add(o.p); return { ...l, [side]: n } }), t))
            }
        }
        const selfCount = activeClashWinner === 'self' ? numSelf : pairCount
        const oppCount  = activeClashWinner === 'opp'  ? numOpp  : pairCount
        schedule('self', selfCount, shotOrigins('self', col, selfBoard[col]))
        schedule('opp',  oppCount,  shotOrigins('opp',  col, oppBoard[col]))
        // Constructions fire nothing but their tiles still fade once the
        // corridor's real shots are away.
        const fadeCon = (side, bf0, last) => {
            const bf = ensureSlots(bf0)
            bf.slots.forEach((t, p, ) => {
                if (t?.kind !== 'baseCon') return
                timers.push(setTimeout(() => setLaunched(l => { const n = new Set(l[side]); n.add(p); return { ...l, [side]: n } }), last + MISSILE_EXCHANGE_GAP_MS))
            })
        }
        fadeCon('self', selfBoard[col], selfCount > 0 ? launchTimeOf('self', selfCount - 1) : 0)
        fadeCon('opp',  oppBoard[col],  oppCount  > 0 ? launchTimeOf('opp',  oppCount  - 1) : 0)
        return () => timers.forEach(clearTimeout)
    }, [isAnimating, clashResult, activeClashId, activeClashContext, activeClashWinner, activeClashSelfStrength, activeClashOppStrength, activeClashSelfTargetIndex, activeClashOppTargetIndex]) // eslint-disable-line react-hooks/exhaustive-deps

    // ── SFX on the same timeline ───────────────────────────────────────────
    const sfxTimersRef = useRef([])
    useEffect(() => () => { sfxTimersRef.current.forEach(clearTimeout); sfxTimersRef.current = []; fadeOutAll() }, [])
    useEffect(() => {
        if (!isAnimating || !activeClashId || clashResult) return
        sfxTimersRef.current.forEach(clearTimeout)
        sfxTimersRef.current = []
        fadeOutAll()
        const numSelf = activeClashSelfStrength ?? 0, numOpp = activeClashOppStrength ?? 0
        const pairCount = Math.min(numSelf, numOpp)
        const winner = activeClashWinner
        const winnerStr = winner === 'self' ? numSelf : winner === 'opp' ? numOpp : pairCount
        const surplus = Math.max(0, winnerStr - pairCount)
        const surplusStart = pairCount * MISSILE_EXCHANGE_MS
        const absorbed = activeClashAbsorbed ?? 0
        const col = selfBoard?.findIndex(bf => bf.id === activeClashId) ?? -1
        const hasTarget = winner === 'self' ? ((targetPlots?.[col] ?? null) !== null || (activeClashSelfTargetIndex ?? null) !== null)
                        : winner === 'opp'  ? (activeClashOppTargetIndex ?? null) !== null
                        : false
        const timers = sfxTimersRef.current
        const at = (ms, name) => timers.push(setTimeout(() => playSound(name), Math.max(0, ms)))
        const atLaunch = (ms, flightMs) => timers.push(setTimeout(() => playSound('launch', { duration: Math.max(0, flightMs) / 1000 }), Math.max(0, ms)))
        for (let k = 0; k < pairCount; k++) {
            atLaunch(k * MISSILE_EXCHANGE_MS, MISSILE_RISE_MS)
            atLaunch(k * MISSILE_EXCHANGE_MS + MISSILE_RESPONSE_MS, MISSILE_RISE_MS - MISSILE_RESPONSE_MS)
            at(k * MISSILE_EXCHANGE_MS + MISSILE_RISE_MS, 'collision')
        }
        for (let j = 0; j < surplus; j++) {
            const launch = hasTarget ? surplusStart + j * MISSILE_SURPLUS_STAGGER_MS : (pairCount + j) * MISSILE_EXCHANGE_MS
            const flightMs = !hasTarget ? MISSILE_RISE_MS : j < absorbed ? MISSILE_SURPLUS_RISE_MS * 0.55 : MISSILE_SURPLUS_RISE_MS
            atLaunch(launch, flightMs)
            if (j < absorbed)   at(launch + MISSILE_SURPLUS_RISE_MS * 0.55, 'collision')
            else if (hasTarget) at(launch + MISSILE_SURPLUS_RISE_MS, 'strike')
        }
    }, [isAnimating, clashResult, activeClashId, activeClashContext, activeClashWinner, activeClashSelfStrength, activeClashOppStrength, activeClashAbsorbed]) // eslint-disable-line react-hooks/exhaustive-deps

    // The stroke pass's per-Base fx lookup for a side (see marks.strokeMarks).
    const baseFxFor = useCallback((side) => (st) => {
        const c = st.footprint?.[0]
        if (c == null) return null
        if (spentBases[side].has(c)) return { fired: true }
        const a = baseAnim[side]
        if (!a) return null
        if (a.spent.includes(c)) return { fired: true }
        if (a.centerS === c) return { fired: false, shakeKey: a.shakeKey }
        return null
    }, [baseAnim, spentBases])

    return { fresh, fireOrigin, freshScan, scanOrigins, fireRing, clearFireRing: () => setFireRing(null), launched, shake, baseFxFor, conCollapse }
}
